﻿import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from 'react-redux';
import { Button } from "components/buttons";
import { FormDateInput, FormRadio, FormTextArea, FormTextInput } from "components/form-fields";
import { Modal, ModalSize, AlertUnsavedData } from "components/notifications";
import { Wizard } from "components/wizard";
import { useValidation } from "hooks/useValidation";
import { UserAccess } from "components/user-access";
import moment from "moment";
import PropTypes from "prop-types";

export function DefectSignoffModal({ loading, isOpen, title, defect, onClose, UserAccessActions, signOffDefect, saving }) {

    const defectOptions = [{ value: "notrectified", label: "Not Rectified" },
    { value: "rectified", label: "Rectified" },
        { value: "monitored", label: "Monitored" }];

    const defaultDefectData = {
        defectSelectedOption: defectOptions[0].value,
        rectified: false,
        monitored: false,
        comment: null,
        maintenanceProvider: null,
        rectifiedBy: null,
        rectifiedDate: null,
        signOffDate: null,
        signOffUser: null
    };
    
    const [isValid, errors, validate] = useValidation();
    const [currentStep, setCurrentStep] = useState(0);
    const emailAddress = useSelector(state => state.loggedInUser.email);
    const [originalDefectData, setOriginalDefectData] = useState(defaultDefectData);
    const [defectSignOff, setDefectSignOff] = useState(defaultDefectData);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (defect) {
            if (defect.rectified) {
                defect.defectSelectedOption = defectOptions[1].value
            } else if (defect.monitored) {
                defect.defectSelectedOption = defectOptions[2].value
            } else {
                defect.rectified = false;
                defect.monitored = false;
                defect.defectSelectedOption = defectOptions[0].value
            }
            defect.comment = defect.rectifiedComment ? defect.rectifiedComment : defect.comment;

            setDefectSignOff(defect);
            setOriginalDefectData(defect);
        }
    }, [defect]);

    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
        }
    }, [isOpen]);

    useEffect(() => {
        const stepRules = [];
        const today = new Date();
        const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

        if (currentStep === 1) {
            stepRules.push({ fieldName: "comment", required: true, maxLength: 4000 });

            if (defectSignOff.rectified) {
                stepRules.push(
                    { fieldName: "maintenanceProvider", required: true },
                    { fieldName: "rectifiedBy", required: true },
                    { fieldName: "rectifiedDate", type: "date", required: true, maxValue: tomorrow }
                );
            }
        }

        validate(stepRules, defectSignOff);

    }, [currentStep, defectSignOff]); 

    const onDefectSignOffChange = (element, data) => {
        setDefectSignOff((prev) => {
            return {
                ...prev,
                [element]: data
            }
        });
    };

    function onDefectOptionChanged(item) {
        setDefectSignOff((prev) => {
            let newOption = item.currentTarget.value;
            if (newOption === originalDefectData.defectSelectedOption) {
                return originalDefectData;
            }

            return {
                ...prev,
                defectSelectedOption: newOption,
                rectified: newOption === "rectified",
                monitored: newOption === "monitored",
                comment: null,
                maintenanceProvider: null,
                rectifiedBy: null,
                rectifiedDate: null
            }
        });
    }

    function checkIsDirty(newObj, oldObj) {
        for (let property in oldObj) {
            if (oldObj.hasOwnProperty(property)) {
                if (typeof oldObj[property] === 'object' && oldObj[property] != null) {
                    let nestedPropertiesDirty = checkIsDirty(newObj[property], oldObj[property]);
                    if (nestedPropertiesDirty) {
                        return true;
                    }
                }
                else if (newObj[property] !== oldObj[property]) {
                    return true;
                }
            }
        }
        return false;
    }

    function onCancel() {
        //Just couldn't get the useDirtyData hook to work here for events, seemed to be contending with the hook in the base Modal Wizard so have implemented a simple version of it inline
        let defectIsDirty = checkIsDirty(originalDefectData, defectSignOff);
        
        if (defectIsDirty) {
            setIsDirty(true);
        } else {
            onClose();
        }
    }

    function onConfirmLeave() {
        setIsDirty(false);
        setDefectSignOff(originalDefectData);
        onClose();
    }

    const getStatusLabel = useMemo(() => {
        if (defectSignOff?.defectSelectedOption) {
            return defectOptions[defectOptions.findIndex(d => d.value === defectSignOff.defectSelectedOption)].label;
        }
        return "";
    }, [defectSignOff?.defectSelectedOption]);

    const getAdditionalButtons = () => {
        return (
            <Button
                intent="secondary"
                text="Cancel"
                onClick={onCancel}
                disabled={loading || saving}
                id="cancel-btn"
            />
        );
    }

    const onSignOffDefect = () => {
        const defectSignOffToUpdate = defectSignOff;
        defectSignOffToUpdate.signOffDate = moment().utc().format("YYYY-MM-DD HH:mm:ss");
        defectSignOffToUpdate.signOffUser = emailAddress;

        signOffDefect(defectSignOffToUpdate);
    }

    const wizardSteps = [
        (
            <div key="step-defect-status">
                <div className="spacer-bottom-medium">
                    <h3>Defect Status</h3>
                    <FormRadio
                        disabled={loading || saving}
                        onChange={(item) => { onDefectOptionChanged(item) }}
                        selectedValue={defectSignOff.defectSelectedOption}
                        loading={loading}
                        options={defectOptions}
                    />
                </div>
            </div>
        ),
        defectSignOff.rectified ? (
            <div key="step-defect-rectified">
                <div className="spacer-bottom-medium">
                    <h3 className="spacer-bottom-medium">Defect Rectified Details</h3>
                    <div className="spacer-bottom-small">
                        <FormTextArea loading={loading || saving} headingText="Action taken to rectify defect" disabled={loading || saving} maxLength={4000} value={defectSignOff.comment} wide onChange={(item) => { onDefectSignOffChange("comment", item.currentTarget.value) }} dangerHelperText={errors["comment"]}></FormTextArea>
                    </div>
                    <div className="inline-items inline-items-space align-items-flex-start spacer-bottom-small">
                        <FormTextInput loading={loading} disabled={loading || saving} headingText="Maintenance Provider" maxLength={255} minLength={1} value={defectSignOff.maintenanceProvider} onChange={(item) => { onDefectSignOffChange("maintenanceProvider", item.currentTarget.value) }} dangerHelperText={errors["maintenanceProvider"]}></FormTextInput>
                        <FormTextInput loading={loading} disabled={loading || saving} headingText="Rectified By" maxLength={255} minLength={1} value={defectSignOff.rectifiedBy} onChange={(item) => { onDefectSignOffChange("rectifiedBy", item.currentTarget.value) }} dangerHelperText={errors["rectifiedBy"]}></FormTextInput>
                    </div>
                    <div className="inline-items">
                        <FormDateInput loading={loading} disabled={loading} headingText="Date Rectifed" value={defectSignOff.rectifiedDate} onChange={(item) => { onDefectSignOffChange("rectifiedDate",moment(item).format("YYYY-MM-DD")) }} dangerHelperText={errors["rectifiedDate"]}
                            minDate={moment("2000-01-01").toDate()}
                            maxDate={moment().toDate()}></FormDateInput>
                    </div>
                </div>
            </div>
        ) : (
            <div key="step-defect-not-rectifed">
                <div className="spacer-bottom-medium">
                        <h3 className="spacer-bottom-medium">Defect {getStatusLabel.toLowerCase()}</h3>
                        <FormTextArea loading={loading} headingText={"Reason the defect is " + getStatusLabel.toLowerCase()} disabled={loading || saving} maxLength={4000} value={defectSignOff.comment} wide onChange={(item) => { onDefectSignOffChange("comment", item.currentTarget.value) }} dangerHelperText={errors["comment"]}></FormTextArea>
                </div>
            </div>
        )
    ];

    return (
        <UserAccess perform={UserAccessActions}
            yes={() => (
                <Modal
                    isOpen={isOpen}
                    title={title}
                    showCloseButton={false}
                    clickOutsideClose={false}
                    size={ModalSize.MEDIUM}
                >
                    <Wizard
                        onStepChange={(stepIndex) => setCurrentStep(stepIndex)}
                        onFinish={onSignOffDefect}
                        canProceed={isValid}
                        disabled={saving}
                        alignButtonsRight
                        additionalButtons={getAdditionalButtons()}
                        finishButtonText="Sign Off Defect"
                    >
                        {wizardSteps}
                    </Wizard>

                    <AlertUnsavedData
                        isDirtySamePage={isDirty}
                        onConfirmLeave={() => { onConfirmLeave() }}
                        onCancelLeave={() => setIsDirty(false)}
                    ></AlertUnsavedData>
                </Modal>
            )}
        />
    );
}

DefectSignoffModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    title: PropTypes.string.isRequired,
    defect: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    UserAccessActions: PropTypes.array.isRequired,
    signOffDefect: PropTypes.func.isRequired,
    saving: PropTypes.bool.isRequired,
};